import type { UUID } from '@appcues/domain-types';
import type { Action } from 'ext/lib/collections';
import { patterns } from './actions';
import type { AccountState } from './types';

const initialState: AccountState = {};

export default function account(
  state: AccountState = initialState,
  action: Action
): AccountState {
  switch (true) {
    case patterns.resolve(action):
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
}

export const selectAccountId = <T extends { account: AccountState }>(
  state: T
): UUID | undefined => state.account.accountId;

export const selectEmail = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account.email;

export const selectFullName = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account.fullName;

export const selectIsSpoofing = <T extends { account: AccountState }>(
  state: T
): boolean | undefined => state.account.isSpoofing;

export const selectUserId = <T extends { account: AccountState }>(
  state: T
): UUID | undefined => state.account.userId;

export const selectVersion = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account.version;

export const selectTier = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account?.tier;

export const selectCreatedAt = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account?.createdAt;

export const selectStudioUrl = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account?.studioUrl;

export const selectFastBaseUrl = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account?.fastBaseUrl;

export const selectRegion = <T extends { account: AccountState }>(
  state: T
): string | undefined => state.account?.region;

/**
 * Whether the current user is an account admin. Populated at builder bootstrap
 * from the `/authn/userinfo` payload via `isAdminFromUserInfo` (see the
 * standalone/extension initial-state wiring), defaulting to `false` until then.
 * Used as a defense-in-depth client gate for admin-only surfaces (the server
 * already 403s non-admins).
 */
export const selectIsAdmin = <T extends { account: AccountState }>(
  state: T
): boolean => state.account?.isAdmin ?? false;
